// ─── Computed State Engine ────────────────────────────────────────────────────
//
// Phase 3 API for derived/computed state. Wraps appState.registerDerived()
// with schema-aware registration that accepts string expressions rather than
// raw compute functions.
//
// The existing stateEngine.ts computes derived state via a caller-supplied
// function. The Phase 3 schema stores expressions as strings (DerivedStateDefinition.expression).
// This engine bridges the gap by compiling schema expressions via compileExpression()
// from stateHydration - the restricted safe evaluator (no eval, no Function()).
//
// All compilation happens at registration time, not at compute time.
// A compile error is thrown immediately on registration - it never silently fails.

import { appState } from './appStateStore';
import { compileExpression, ExpressionCompileError } from './stateHydration';
import type { DerivedStateDefinition } from '@/core/schema/stateSchema';
import type { StateValue } from '@/appEngine/stateEngine';

export { ExpressionCompileError };

// ─── Computed State Engine ────────────────────────────────────────────────────

class ComputedStateEngine {
  private registered = new Map<string, { expression: string; deps: string[] }>();

  /**
   * Register a single derived state definition.
   * Compiles the expression immediately - throws ExpressionCompileError on invalid syntax.
   */
  register(def: DerivedStateDefinition): void {
    const compute = compileExpression(def.expression);
    this.registered.set(def.key, { expression: def.expression, deps: def.dependsOn });
    appState.registerDerived({
      id: def.key,
      dependencies: def.dependsOn,
      compute,
    });
  }

  /** Register all derived definitions from a schema map. */
  registerAll(defs: DerivedStateDefinition[]): void {
    for (const def of defs) {
      this.register(def);
    }
  }

  /** Get the current computed value for a key. */
  evaluate(key: string): StateValue {
    return appState.get(key);
  }

  /**
   * Return the dependency graph: key → dependsOn[].
   * Used by logicGraph (Layer 2C) and diagnostics.
   */
  getDependencyGraph(): Map<string, string[]> {
    const graph = new Map<string, string[]>();
    for (const [key, meta] of this.registered) {
      graph.set(key, [...meta.deps]);
    }
    return graph;
  }

  /** Returns true if a key is a registered derived state. */
  isDerived(key: string): boolean {
    return this.registered.has(key);
  }

  clear(): void {
    this.registered.clear();
  }
}

export const computedStateEngine = new ComputedStateEngine();
